import apiClient from './authService';

/**
 * Feature Flag Service
 *
 * Handles checking feature flag status from the backend
 */

const featureFlagService = {
  // Cache for feature flags to avoid excessive API calls
  cache: {
    flags: null,
    timestamp: null,
    ttl: 5 * 60 * 1000 // 5 minutes
  },

  /**
   * Get all feature flags enabled for the current user (or public)
   * @returns {Promise<Object>} Object with feature keys and boolean values
   */
  getEnabledFeatures: async () => {
    // Check cache first
    if (featureFlagService.cache.timestamp &&
        Date.now() - featureFlagService.cache.timestamp < featureFlagService.cache.ttl &&
        featureFlagService.cache.flags) {
      return featureFlagService.cache.flags;
    }

    try {
      const response = await apiClient.get('/api/feature-flags/me');

      // Update cache
      featureFlagService.cache.flags = response.data;
      featureFlagService.cache.timestamp = Date.now();

      return response.data;
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      // Return all disabled if error
      return {
        REGISTRATION_ENABLED: false,
        LOGIN_ENABLED: false,
        MAINTENANCE_CHAT_ENABLED: false,
        PROPERTY_MANAGEMENT_ENABLED: false,
        TENANT_PORTAL_ENABLED: false,
        PAYMENTS_ENABLED: false,
        ANALYTICS_ENABLED: false
      };
    }
  },

  /**
   * Check if a specific feature is enabled
   * @param {string} featureKey - The feature flag key (e.g., 'REGISTRATION_ENABLED')
   * @returns {Promise<boolean>}
   */
  isFeatureEnabled: async (featureKey) => {
    const flags = await featureFlagService.getEnabledFeatures();
    return flags[featureKey] || false;
  },

  /**
   * Check if registration is enabled
   * @returns {Promise<boolean>}
   */
  isRegistrationEnabled: async () => {
    return await featureFlagService.isFeatureEnabled('REGISTRATION_ENABLED');
  },

  /**
   * Check if login is enabled
   * @returns {Promise<boolean>}
   */
  isLoginEnabled: async () => {
    return await featureFlagService.isFeatureEnabled('LOGIN_ENABLED');
  },

  /**
   * Clear the feature flag cache (useful after login/logout)
   */
  clearCache: () => {
    featureFlagService.cache.flags = null;
    featureFlagService.cache.timestamp = null;
  }
};

export default featureFlagService;
